import { useState, useEffect } from 'react';

/**
 * Returns true when the user is currently interacting with the app on a
 * tablet — that is, a wide-screen device whose primary input right now is
 * touch rather than a mouse/trackpad.
 *
 * Covers three device families:
 *   1. iPad (legacy iOS UA) — UA-sniff "iPad".
 *   2. iPadOS 13+ (reports a desktop "Macintosh" UA by default) — Macintosh
 *      UA combined with > 1 touch point is unique to iPad.
 *   3. Hybrid + Android tablets (Surface Pro, Galaxy Tab, etc.) — wide
 *      viewport AND primary pointer is coarse. `pointer: coarse` is the
 *      key here: it reflects the *active* input modality. On a Surface
 *      Pro with the Type Cover attached, the OS reports pointer:fine and
 *      this returns false (so the user gets the desktop UI). Detach the
 *      keyboard and the OS flips to pointer:coarse, returning true.
 *
 * Why not just trust `maxTouchPoints`? Because a Surface running with a
 * keyboard, or any Windows desktop with a touchscreen monitor, has
 * touch hardware but the user is plainly driving with a mouse — and the
 * old logic was returning "tablet" for those, which is wrong.
 *
 * The hook re-evaluates on resize and on pointer-mode change, so users who
 * dock/undock a hybrid mid-session get the right UI without a page refresh.
 *
 * Lives in its own file so Vite's Fast Refresh boundary stays clean —
 * JobsTabletView.jsx must export only React components for HMR to refresh
 * it cleanly, and mixing this hook in there breaks that rule.
 */

const TABLET_MIN_WIDTH = 768;

const detectIsTablet = () => {
    // Server-side / non-browser guard — every browser API below assumes window/navigator.
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

    const ua = navigator.userAgent || '';
    const maxTouchPoints = navigator.maxTouchPoints || 0;

    // 1. Classic iPad — pre-iPadOS 13 still emits "iPad" in the UA.
    const isIpadUA = /iPad/.test(ua);

    // 2. iPadOS 13+ — masquerades as desktop Safari (Macintosh UA) but is
    //    the only "Mac" with multi-touch hardware. The touch-point check is
    //    what disambiguates an actual Mac from an iPad.
    const isIpadOSDesktopMode = /Macintosh/.test(ua) && maxTouchPoints > 1;

    if (isIpadUA || isIpadOSDesktopMode) return true;

    // 3. Hybrid (Surface Pro) + Android tablets — combine three signals:
    //    - Wide enough to warrant the tablet layout (phones are excluded).
    //    - Pointer is currently coarse, i.e. the user is actually touching
    //      the screen, not driving with a mouse/trackpad. This is what
    //      makes a Surface with Type Cover correctly return false.
    //    - Touch hardware is present, as a sanity check: rules out the
    //      bizarre case of a non-touch device that happens to report coarse.
    const coarsePointer = typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches;
    const noHover = typeof window.matchMedia === 'function'
        && window.matchMedia('(hover: none)').matches;
    const wideEnough = window.innerWidth >= TABLET_MIN_WIDTH;

    // Require coarse pointer at minimum — that's the "user is touching"
    // signal. noHover strengthens it but isn't required, since older Edge/
    // Firefox versions are inconsistent about the hover MQ.
    if (wideEnough && coarsePointer && (noHover || maxTouchPoints > 0)) return true;

    return false;
};

const useIsTablet = () => {
    const [isTablet, setIsTablet] = useState(detectIsTablet);

    useEffect(() => {
        const handler = () => setIsTablet(detectIsTablet());

        // Resize fires whenever the window changes shape, which on hybrids
        // is the most obvious "something changed" signal — docking a Surface
        // often resizes the viewport as DPI/orientation flip.
        window.addEventListener('resize', handler);

        // Listen for pointer-mode flips too. When a Surface user detaches
        // the keyboard, the OS reports pointer:coarse via this MQ — without
        // this listener the UI would stay stuck on desktop until the next
        // resize. matchMedia.addEventListener is the modern API; the older
        // addListener is a fallback for browsers that haven't caught up.
        let coarseMQ = null;
        let hoverMQ  = null;
        if (typeof window.matchMedia === 'function') {
            coarseMQ = window.matchMedia('(pointer: coarse)');
            hoverMQ  = window.matchMedia('(hover: none)');
            if (coarseMQ.addEventListener) {
                coarseMQ.addEventListener('change', handler);
                hoverMQ.addEventListener('change', handler);
            } else if (coarseMQ.addListener) {
                // Safari < 14 / older Edge — legacy addListener API.
                coarseMQ.addListener(handler);
                hoverMQ.addListener(handler);
            }
        }

        return () => {
            window.removeEventListener('resize', handler);
            if (coarseMQ) {
                if (coarseMQ.removeEventListener) {
                    coarseMQ.removeEventListener('change', handler);
                    hoverMQ.removeEventListener('change', handler);
                } else if (coarseMQ.removeListener) {
                    coarseMQ.removeListener(handler);
                    hoverMQ.removeListener(handler);
                }
            }
        };
    }, []);

    return isTablet;
};

export { useIsTablet };
export default useIsTablet;